<script setup lang="ts">
import { onUnmounted } from 'vue';

import TableFooterBar from '@/components/bars/TableFooterBar.vue';
import KBtn from '@/components/btns/KBtn.vue';
import TablePageFooter from '@/components/footers/TablePageFooter.vue';
import ExportAsCsvIconBtn from '@/components/iconBtns/ExportAsCsvIconBtn.vue';
import BookedAirRoadSearchForm from '@/components/searchForms/BookedAirRoadSearchForm.vue';
import BookedAirRoadTable from '@/components/tables/BookedAirRoadTable.vue';
import FormWrapper from '@/components/wrappers/FormWrapper.vue';
import PageContentWrapper from '@/components/wrappers/PageContentWrapper.vue';
import TablePageShadowWrapper from '@/components/wrappers/TablePageShadowWrapper.vue';
import useBookedAirRoadViewData from '@/composables/data/useBookedAirRoadViewData';
import useCommonRouteQuery from '@/composables/routeQueries/useCommonRouteQuery';
import useCurrentDateString from '@/composables/useCurrentDateString';
import { useBookedAirRoadViewStore } from '@/stores/useBookedAirRoadViewStore';
import { defCurPage, defPageSize } from '@/constants/routeQuery';

const { getLast7DaysNext5DaysRange } = useCurrentDateString();
const routeQuery = useCommonRouteQuery({
  queryDates: getLast7DaysNext5DaysRange(),
  queryCurPage: defCurPage,
  queryPageSize: defPageSize,
  queryDateType: 'rh_job_date', // ar_created, required
});

const bookedAirRoadViewStore = useBookedAirRoadViewStore();
bookedAirRoadViewStore.init(useBookedAirRoadViewData(routeQuery), routeQuery);

onUnmounted(function () {
  bookedAirRoadViewStore.$reset();
});
</script>

<template>
  <div :class="$style.bookedAirRoadView" data-testid="bookedAirRoadView">
    <PageContentWrapper>
      <FormWrapper :class="$style.formWrapper">
        <BookedAirRoadSearchForm
          :initial-data="bookedAirRoadViewStore.initialData"
          :loading="bookedAirRoadViewStore.data.isFetchingAirRoadBookedJobsData"
          :class="$style.form"
          @submit="bookedAirRoadViewStore.search"
        />
      </FormWrapper>
      <TablePageShadowWrapper :class="$style.tablePageShadowWrapper">
        <BookedAirRoadTable
          v-model="bookedAirRoadViewStore.selectedResults"
          :data="bookedAirRoadViewStore.bookedAirRoadTableData"
          :loading="bookedAirRoadViewStore.data.isFetchingAirRoadBookedJobsData"
        />
        <TablePageFooter
          v-if="bookedAirRoadViewStore.data.airRoadBookedJobsData?.results"
          :cur-page="bookedAirRoadViewStore.query.curPage"
          :page-size="bookedAirRoadViewStore.query.pageSize"
          :total="bookedAirRoadViewStore.data.airRoadBookedJobsData.count"
        >
          <ExportAsCsvIconBtn
            :disabled="
              bookedAirRoadViewStore.data.isFetchingAirRoadBookedJobsData ||
              !bookedAirRoadViewStore.data.airRoadBookedJobsData?.results
                ?.length
            "
            :loading="bookedAirRoadViewStore.isDownloadingCSV"
            @click="bookedAirRoadViewStore.downloadCSV"
          />
        </TablePageFooter>
      </TablePageShadowWrapper>
    </PageContentWrapper>

    <TableFooterBar>
      <KBtn
        size="lg"
        append-icon="mdi-download"
        :disabled="
          bookedAirRoadViewStore.data.isFetchingAirRoadBookedJobsData ||
          !bookedAirRoadViewStore.data.airRoadBookedJobsData?.results?.length ||
          !bookedAirRoadViewStore.isSelected
        "
        :loading="bookedAirRoadViewStore.isFetchingBulkLabels"
        @click="bookedAirRoadViewStore.handleDownloadBulkLabelsClick"
      >
        Download bulk labels
      </KBtn>
    </TableFooterBar>
  </div>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.bookedAirRoadView {
}
  .formWrapper {
    padding: 2.4rem 1.5rem 0.5rem 1.5rem;
    max-width: calc(100vw - variables.$sideMainBarWidth - (variables.$pageMarginX * 2));
    margin: 2rem 0;
  }
  .tablePageShadowWrapper {
    max-width: calc(100vw - variables.$sideMainBarWidth - (variables.$pageMarginX * 2));
  }
</style>
